import { useState, type SyntheticEvent } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { apiClient } from "../lib/apiClient";
import { docUrl } from "../lib/docLinks";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import type { WorkingScopeSearch } from "../lib/workingScope";
import {
  Button,
  HelpTip,
  InlineError,
  Page,
  PageHeader,
  Section,
  Skeleton,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../ui";
import { AlertRuleAuthoringLeaveGuard } from "./alerts/AlertRuleAuthoringLeaveGuard";

export interface AlertRuleSilenceViewProps {
  errorMessage?: string;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onUntilChange: (value: string) => void;
  rule: {
    display_name: string;
    silence_expires_at?: string | null;
  };
  until: string;
}

export function AlertRuleSilenceView({
  errorMessage,
  isPending,
  onCancel,
  onSubmit,
  onUntilChange,
  rule,
  until,
}: AlertRuleSilenceViewProps) {
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit();
  }

  // Route-level Authoring (UI-LAW §4c): Cancel and the primary action ride
  // the PageHeader, with the request error immediately left of the buttons.
  return (
    <form onSubmit={submit}>
      <Stack gap={7}>
        <PageHeader
          actions={
            <>
              {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
              <Button
                disabled={isPending}
                onClick={onCancel}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button disabled={isPending || until.length === 0} type="submit">
                {isPending ? "Silencing..." : "Silence rule"}
              </Button>
            </>
          }
          compact
        />
        <Section
          heading="Silence window"
          help={
            <HelpTip
              docHref={docUrl("concepts/alert-rules")}
              docLabel="Alert rules"
              label="About alert silencing"
            >
              {"Mutes delivery. Evaluation keeps running."}
            </HelpTip>
          }
        >
          <Stack gap={4}>
            <Text>{rule.display_name}</Text>
            {rule.silence_expires_at ? (
              <Text variant="muted">
                Currently silenced until{" "}
                {formatDateTime(rule.silence_expires_at)}.
              </Text>
            ) : (
              <Text variant="muted">This rule is not silenced.</Text>
            )}
            <TextInput
              disabled={isPending}
              label="Silence until"
              required
              type="datetime-local"
              value={until}
              onChange={(event) => {
                onUntilChange(event.currentTarget.value);
              }}
            />
          </Stack>
        </Section>
      </Stack>
    </form>
  );
}

export function AlertRuleSilence() {
  const { ruleId } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [until, setUntil] = useState("");
  const rule = useQuery({
    enabled: typeof ruleId === "string",
    queryKey: queryKeys.alertRules.detail(ruleId ?? "pending"),
    queryFn: async () => {
      if (typeof ruleId !== "string") {
        throw new Error("Alert rule id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/alert-rules/{id}", {
        params: { path: { id: ruleId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const silence = useMutation({
    mutationFn: async (untilIso: string) => {
      if (typeof ruleId !== "string") {
        throw new Error("Alert rule id is required.");
      }
      const { data, error } = await apiClient.POST(
        "/v1/alert-rules/{id}:silence",
        {
          body: { until: untilIso },
          params: { path: { id: ruleId } },
        },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (updatedRule) => {
      queryClient.setQueryData(
        queryKeys.alertRules.detail(updatedRule.id),
        updatedRule,
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.alertRules.all,
      });
      toast.success("Alert rule silenced");
    },
  });

  function returnToRule() {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) => ({
        agent,
        rule: typeof ruleId === "string" ? ruleId : undefined,
        tab: "rules" as const,
        team,
      }),
      to: "/alerts",
    });
  }

  async function submit() {
    const parsed = new Date(until);
    if (!Number.isFinite(parsed.getTime())) {
      return;
    }
    await silence.mutateAsync(parsed.toISOString());
    returnToRule();
  }

  return (
    <Page variant="config">
      <AlertRuleAuthoringLeaveGuard
        isDirty={until.length > 0 && !silence.isSuccess}
      />
      {rule.isPending ? (
        <Skeleton label="Loading alert rule" rows={4} shape="detail-field" />
      ) : rule.isError ? (
        <InlineError>{rule.error.message}</InlineError>
      ) : (
        <AlertRuleSilenceView
          errorMessage={
            silence.isError
              ? requestErrorDescription(silence.error, "Alert silence failed.")
              : undefined
          }
          isPending={silence.isPending}
          onCancel={returnToRule}
          onSubmit={() => {
            void submit().catch(() => undefined);
          }}
          onUntilChange={setUntil}
          rule={rule.data}
          until={until}
        />
      )}
    </Page>
  );
}
